import { ColumnDef } from '@tanstack/react-table';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

const APPENDED_ROW_ID = 'APPENDED_ROW_ID';

const headerCell = (label: string) => (
  <span className="text-nowrap">{label}</span>
);

const CriticalResourceRequestsTable = ({
  statusSummaryId,
  originalCriticalResourceRequests = [],
}: {
  statusSummaryId: string;
  originalCriticalResourceRequests?: { criticalResourceRequest: string }[];
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rowBeingEditedId, setRowBeingEditedId] = useState<string | null>(null);
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      criticalResourceRequests: originalCriticalResourceRequests,
    },
  });

  const { control, register, getValues } = form;

  const { append, remove, update, fields } = useFieldArray<{
    criticalResourceRequests: { criticalResourceRequest: string }[];
  }>({
    control,
    name: 'criticalResourceRequests',
  });

  const rows = useMemo(() => {
    const filteredFields = fields.filter((field) =>
      field.criticalResourceRequest.toLowerCase().includes(search.toLowerCase())
    );

    const finalField = fields[fields.length - 1];

    if (
      finalField &&
      !filteredFields.some((field) => field.id === finalField.id) &&
      rowBeingEditedId === APPENDED_ROW_ID
    ) {
      return filteredFields.concat(finalField);
    }

    return filteredFields;
  }, [fields, search, rowBeingEditedId]);

  const allSelected = useMemo(() => {
    if (!rows.length) return false;
    return rows.length === selectedIds.length;
  }, [rows, selectedIds]);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(allSelected ? [] : rows.map((field) => field.id));
  }, [allSelected, rows]);

  const toggleRow = useCallback(
    (id: string) => {
      const set = new Set(selectedIds);
      if (set.has(id)) {
        set.delete(id);
      } else {
        set.add(id);
      }
      setSelectedIds([...set]);
    },
    [selectedIds]
  );

  const deleteSelected = useCallback(() => {
    const toDeleteIndexes = fields.reduce<number[]>((acc, field, index) => {
      if (selectedIds.includes(field.id)) {
        acc.push(index);
      }
      return acc;
    }, []);

    upsertIncidentStatusSummary({
      criticalResourceRequests: getValues('criticalResourceRequests')
        .filter(
          (_: { criticalResourceRequest: string }, index: number) =>
            !toDeleteIndexes.includes(index)
        )
        .map((field) => field.criticalResourceRequest),
    });
    remove(toDeleteIndexes);
    setSelectedIds([]);
  }, [remove, fields, selectedIds, upsertIncidentStatusSummary, getValues]);

  const deleteRow = useCallback(
    (rowToDelete: { id: string; criticalResourceRequest: string }) => {
      const index = fields.findIndex((field) => field.id === rowToDelete.id);
      if (index !== -1) {
        remove(index);
      }
      setSelectedIds((prev) => prev.filter((x) => x !== rowToDelete.id));
      upsertIncidentStatusSummary({
        criticalResourceRequests: getValues('criticalResourceRequests')
          .filter(
            (_: { criticalResourceRequest: string }, idx: number) =>
              idx !== index
          )
          .map((field) => field.criticalResourceRequest),
      });
    },
    [remove, fields, upsertIncidentStatusSummary, getValues]
  );

  const isRowEditable = useCallback(
    (rowId: string, rowIndex: number): boolean => {
      return (
        rowBeingEditedId === rowId ||
        (rowBeingEditedId === APPENDED_ROW_ID && rowIndex === rows.length - 1)
      );
    },
    [rowBeingEditedId, rows.length]
  );

  const appendBlankResourceRequest = useCallback(() => {
    append({ criticalResourceRequest: '' });
    setRowBeingEditedId(APPENDED_ROW_ID);
  }, [append, setRowBeingEditedId]);

  const columns = useMemo<
    ColumnDef<{ id: string; criticalResourceRequest: string }>[]
  >(
    () => [
      {
        id: 'select',
        header: () => (
          <StylishNewCheckbox
            checked={allSelected}
            onChange={toggleSelectAll}
            disabled={rows.length === 0 || rowBeingEditedId !== null}
          />
        ),
        cell: ({ row }) => (
          <StylishNewCheckbox
            checked={selectedIds.includes(row.original.id)}
            onChange={() => {
              toggleRow(row.original.id);
            }}
            disabled={rowBeingEditedId !== null}
          />
        ),
        size: 10,
        enableSorting: false,
        enableResizing: false,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'criticalResourceRequest',
        header: () => headerCell('Resource Request'),
        cell: ({ row }) => {
          if (isRowEditable(row.original.id, row.index)) {
            const fieldIndex = fields.findIndex(
              (field) => field.id === row.original.id
            );

            return (
              <StylishNewInput
                className="form-control"
                placeholder="Enter resource request"
                defaultValue={row.original.criticalResourceRequest}
                {...register(
                  `criticalResourceRequests.${fieldIndex}.criticalResourceRequest`
                )}
              />
            );
          } else {
            return <span>{row.original.criticalResourceRequest}</span>;
          }
        },
        enableSorting: true,
        enableResizing: false,
        size: 300,
        meta: { headerAlign: 'left' },
      },

      {
        id: 'actions',
        header: () => <span />,
        cell: ({ row }) => {
          if (isRowEditable(row.original.id, row.index)) {
            const fieldIndex = fields.findIndex(
              (field) => field.id === row.original.id
            );

            return (
              <div className="d-flex gap-1 justify-content-end">
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();

                    const currentValues = getValues('criticalResourceRequests');
                    upsertIncidentStatusSummary({
                      criticalResourceRequests: currentValues.map(
                        (field) => field.criticalResourceRequest
                      ),
                    });
                    update(fieldIndex, {
                      criticalResourceRequest:
                        currentValues[fieldIndex].criticalResourceRequest,
                    });
                    setRowBeingEditedId(null);
                  }}
                >
                  <SharedIcon iconName="check" classes="me-1" />
                </StylishNewButton>
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    update(fieldIndex, {
                      criticalResourceRequest:
                        row.original.criticalResourceRequest,
                    });
                    setRowBeingEditedId(null);
                  }}
                >
                  <SharedIcon iconName="cancel" classes="me-1" />
                </StylishNewButton>
              </div>
            );
          } else {
            return (
              <div className="d-flex gap-1 justify-content-end">
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedIds([]);
                    setRowBeingEditedId(row.original.id);
                  }}
                  disabled={rowBeingEditedId !== null}
                >
                  <SharedIcon iconName="edit" classes="me-1" />
                </StylishNewButton>
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteRow(row.original);
                  }}
                  disabled={rowBeingEditedId !== null}
                >
                  <SharedIcon iconName="delete" classes="me-1" />
                </StylishNewButton>
              </div>
            );
          }
        },
        enableSorting: false,
        size: 160,
        meta: { headerAlign: 'right' },
      },
    ],
    [
      allSelected,
      selectedIds,
      toggleRow,
      toggleSelectAll,
      register,
      deleteRow,
      rowBeingEditedId,
      setRowBeingEditedId,
      isRowEditable,
      getValues,
      update,
      upsertIncidentStatusSummary,
      fields,
      rows.length,
    ]
  );

  return (
    <>
      <FormProvider {...form}>
        <div className="d-flex flex-column justify-content-between flex-grow-1">
          <div className="d-flex justify-content-between align-items-end mb-2 gap-2 flex-wrap">
            <div
              className="flex-grow-1 justify-content-between align-items-center gap-2 d-flex"
              style={{ minWidth: 240 }}
            >
              <label style={{ whiteSpace: 'nowrap' }}>
                Critical Resource Requests & Unmet Needs
              </label>
              <input
                className="form-control"
                placeholder="Search resource requests..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={{ paddingLeft: '4rem' }}>
              <StylishNewButton
                className="button button--primary button--sml"
                onClick={(e) => {
                  e.stopPropagation();
                  appendBlankResourceRequest();
                }}
                disabled={rowBeingEditedId !== null}
              >
                <SharedIcon iconName="add" classes="me-2" />
                Add Resource Request
              </StylishNewButton>
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>{selectedIds.length} selected</div>
              <StylishNewButton
                className="button--error button--sml"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteSelected();
                }}
              >
                <SharedIcon iconName="delete" classes="me-1" /> Delete Selected
              </StylishNewButton>
            </div>
          )}
          <PratusTable
            className="layout-auto pratus-table"
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            noDataIndication={
              <div className="text-center p-2">
                {search
                  ? 'No results!'
                  : 'There are no resource requests yet. Click + Add Resource Request to add a resource request.'}
              </div>
            }
            footer={
              <span className="text-nowrap me-2">{`${rows.length} resource requests`}</span>
            }
          />
        </div>
      </FormProvider>
    </>
  );
};

export default CriticalResourceRequestsTable;
